import {
  DateTimePickerProps,
  DateTimeValidationError,
  DateView,
  LocalizationProvider,
  PickerChangeHandlerContext,
  TimeView,
} from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker'
import { TimePicker, TimePickerProps } from '@mui/x-date-pickers/TimePicker'
import { DateTime } from 'luxon'
import React, { useEffect, useState } from 'react'

import { useComplatibleLocale } from '../../hooks/useCompatibleLocale'
import { useRtlLocaleCheck } from '../../hooks/useRtlLocaleCheck'
import { styled } from '../../theme'
import { THEME_CONST } from '../../theme'
import { MsdkStyledComponent } from '../../utils'

export const StyledDatePicker: MsdkStyledComponent<DatePickerProps<any>> = styled(DatePicker)``
export const StyledTimePicker: MsdkStyledComponent<TimePickerProps<any>> = styled(TimePicker)``

const Wrapper = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  .MuiInputBase-root {
    border-radius: ${THEME_CONST.inputBorderRadius}px;
  }
`

type Props = {
  disabled?: boolean
  dateLabel: React.ReactNode
  timeLabel: React.ReactNode
  locale?: string
  value: any
  views?: DateTimePickerProps<any>['views']
  onChange?: (value: any, context?: PickerChangeHandlerContext<DateTimeValidationError>) => void
  slotProps?: DateTimePickerProps<any>['slotProps']
}

function isDateView(arg: string): arg is DateView {
  return ['year', 'month', 'day'].some((e) => e === arg)
}
function isTimeView(arg: string): arg is TimeView {
  return ['hours', 'minutes', 'seconds'].some((e) => e === arg)
}

export function SplitDateTimePicker(props: Props) {
  const { disabled, dateLabel, onChange, slotProps, timeLabel, value, views } = props

  const parseValue = (val: any): DateTime | null => {
    if (val instanceof DateTime) {
      return val
    }
    try {
      const parsed = DateTime.fromISO(val.toString())
      return parsed.isValid ? parsed : null
    } catch {
      return null
    }
  }

  const [date, setDate] = useState<DateTime | null>(parseValue(value)?.set({ hour: 0, minute: 0, second: 0 }) ?? null)
  const [time, setTime] = useState<DateTime | null>(parseValue(value))
  const { locale = DateTime.local().locale } = props

  const compatibleLocale = useComplatibleLocale(locale)
  const isRtl = useRtlLocaleCheck(locale)

  useEffect(() => {
    const parsedValue = parseValue(value)
    if (parsedValue) {
      setDate(parsedValue.set({ hour: 0, minute: 0, second: 0 }))
      setTime(parsedValue)
    }
  }, [value])

  const getDateViews = () => {
    const dateViews: DateView[] = []
    views?.map((view) => isDateView(view) && dateViews.push(view))
    return dateViews.length ? dateViews : undefined
  }

  const getTimeViews = () => {
    const timeViews: TimeView[] = []
    views?.map((view) => isTimeView(view) && timeViews.push(view))
    return timeViews.length ? timeViews : undefined
  }

  const dateSlotProps = {
    ...slotProps,
    textField: {
      ...slotProps?.textField,
      inputProps: {
        ...(slotProps?.textField as Record<string, any>)?.inputProps,
        dir: isRtl ? 'rtl' : undefined,
      },
    },
  } as unknown as DatePickerProps<any>['slotProps']
  const timeSlotProps = {
    ...slotProps,
    textField: {
      ...slotProps?.textField,
      inputProps: {
        ...(slotProps?.textField as Record<string, any>)?.inputProps,
        dir: isRtl ? 'rtl' : undefined,
      },
    },
  } as unknown as TimePickerProps<any>['slotProps']

  const combineDateAndTime = (newDate: DateTime, newTime: DateTime): DateTime => {
    return newDate.set({
      hour: newTime.hour,
      minute: newTime.minute,
      second: newTime.second,
    })
  }

  const handleDateChange = (newDate: DateTime | null) => {
    setDate(newDate)

    if (newDate && time) {
      const combinedDateTime = combineDateAndTime(newDate, time)
      onChange?.(combinedDateTime)
    }
  }

  const handleTimeChange = (newTime: DateTime | null) => {
    setTime(newTime)

    if (newTime && date) {
      const combinedDateTime = combineDateAndTime(date, newTime)
      onChange?.(combinedDateTime)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={compatibleLocale}>
      <Wrapper>
        <StyledDatePicker
          onChange={(d) => handleDateChange(d)}
          disabled={disabled}
          label={dateLabel}
          value={date}
          views={getDateViews()}
          slotProps={dateSlotProps}
        />
        <StyledTimePicker
          onChange={(t) => handleTimeChange(t)}
          disabled={disabled || getTimeViews() === undefined}
          value={time}
          label={timeLabel}
          views={getTimeViews()}
          slotProps={timeSlotProps}
        />
      </Wrapper>
    </LocalizationProvider>
  )
}
